import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { CurrencyProvider } from '@sdk/common/currency/CurrencyContext';
import { AuthProvider } from '@sdk/authentication/AuthContext';
import { EXTERNAL_PAYMENT_TYPES } from '@sdk/account/constants';

import { BillingInfoProvider } from '@web/account/billing/BillingInfoContext';
import { SubscriptionUtilsType } from '@web/account/types';
import BillingPlans from '@web/account/components/BillingPlans';
import BillingPlansBanner from '@web/account/billing/BillingPlansBanner/BillingPlansBanner';
import EditBilling from '@web/account/billing/EditBilling/EditBilling';
import getConfig from '@web/config/getConfig';
import { STRIPE_ELEMENTS_DEFAULT_OPTIONS } from '@web/account/constants';
import { BillingViewProvider } from '@web/account/billing/BillingViewContext/BillingViewContext';

const config = getConfig();
const stripePromise = loadStripe(config.STRIPE_PUBLIC_KEY);

type Props = {
  billingInfo: BillingInfoType;
  subscriptionUtils: SubscriptionUtilsType;
  currency: CurrencyType;
  currentUser: AuthUserType;
  isOldPremium: boolean;
  showPricing: boolean;
  fromBillingAddressModal: boolean;
  startTrial: () => Promise<void>;
  goToPurchase: (plan: number, isAnnual: boolean) => void;
  goToBilling: () => void;
  goToPricing: () => void;
  onBillingInfoUpdated: (billingInfo: BillingInfoType) => void;
  shouldShowAddressErrors: boolean;
};

const BillingView = ({
  billingInfo,
  subscriptionUtils,
  currency,
  currentUser,
  startTrial,
  showPricing,
  fromBillingAddressModal,
  goToPurchase,
  goToBilling,
  goToPricing,
  onBillingInfoUpdated,
  shouldShowAddressErrors,
}: Props) => {
  const { isToastBilling } = currentUser.org;

  const stripeElementsOptions = {
    ...STRIPE_ELEMENTS_DEFAULT_OPTIONS,
    ...(billingInfo.paymentFailedClientSecret
      ? {
          clientSecret: billingInfo.paymentFailedClientSecret,
          allowedSourceTypes: ['card'],
        }
      : {
          mode: 'subscription',
          currency: 'usd',
          amount: Math.max(0, Math.floor(billingInfo.nextChargeAmount * 100)),
          paymentMethodCreation: 'manual',
        }),
  } as StripeElementsOptions;

  const isExternalPaymentType =
    isToastBilling ||
    (billingInfo?.paymentType && billingInfo.paymentType in EXTERNAL_PAYMENT_TYPES);

  if (
    (!billingInfo.subscriptionStartDate &&
      !billingInfo.scheduledToResumeDate &&
      !billingInfo.cancelledAutomatically) ||
    showPricing ||
    isExternalPaymentType
  ) {
    return (
      <Elements stripe={stripePromise} options={stripeElementsOptions}>
        <BillingInfoProvider billingInfo={billingInfo} onBillingInfoUpdated={onBillingInfoUpdated}>
          <AuthProvider currentUser={currentUser}>
            <CurrencyProvider>
              {!showPricing && !isToastBilling && <BillingPlansBanner showPricing={showPricing} />}
              <BillingPlans
                orgSettings={currentUser.orgSettings}
                startTrial={startTrial}
                goToPurchase={goToPurchase}
                currency={currency}
                subscriptionUtils={subscriptionUtils}
                showPricing={showPricing}
                goToBilling={goToBilling}
              />
            </CurrencyProvider>
          </AuthProvider>
        </BillingInfoProvider>
      </Elements>
    );
  }

  return (
    <Elements stripe={stripePromise} options={stripeElementsOptions}>
      <BillingInfoProvider billingInfo={billingInfo} onBillingInfoUpdated={onBillingInfoUpdated}>
        <AuthProvider currentUser={currentUser}>
          <BillingViewProvider
            isEditBillingInformationActive={fromBillingAddressModal}
            shouldShowAddressErrors={shouldShowAddressErrors}>
            <EditBilling
              subscriptionUtils={subscriptionUtils}
              currency={currency}
              orgSettings={currentUser.orgSettings}
              goToPricing={goToPricing}
            />
          </BillingViewProvider>
        </AuthProvider>
      </BillingInfoProvider>
    </Elements>
  );
};

export default BillingView;
